import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Checkbox } from '../../components/ui/checkbox';
import { 
  Package, Plus, ArrowLeft, Camera, Sparkles, Trash2, Save,
  AlertTriangle, QrCode, Image as ImageIcon, Shield, Upload,
  Sofa, Bed, Lamp, Tv, Utensils, BookOpen, Shirt, Box
} from 'lucide-react';
import api from '../../utils/api';

// Category icons mapping
const CATEGORY_ICONS = {
  'Living Room': Sofa,
  'Bedroom': Bed,
  'Kitchen': Utensils,
  'Bathroom': Box,
  'Office': BookOpen,
  'Electronics': Tv,
  'Clothing': Shirt,
  'Lighting': Lamp,
  'Fragile': AlertTriangle,
  'Other': Box,
};

const CustomerInventoryPage = ({ user }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const jobId = searchParams.get('jobId');
  const fileInputRef = useRef(null);
  const qrInputRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [job, setJob] = useState(null);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Dialogs
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showPhotoDialog, setShowPhotoDialog] = useState(false);
  const [showPackingDialog, setShowPackingDialog] = useState(false);
  const [showAIDialog, setShowAIDialog] = useState(false);
  const [showQRDialog, setShowQRDialog] = useState(false);
  
  // Current item being edited
  const [currentItem, setCurrentItem] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [aiScanning, setAIScanning] = useState(false);

  useEffect(() => {
    if (!jobId) {
      toast.error('No job selected');
      navigate('/mobile/dashboard');
      return;
    }
    fetchData();
  }, [jobId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch job details
      const jobResponse = await api.get(`/jobs/${jobId}`);
      setJob(jobResponse.data);
      
      // Fetch existing inventory
      const invResponse = await api.get(`/jobs/${jobId}/inventory`);
      setInventoryItems(invResponse.data || []);
      
      // Fetch templates
      const templatesResponse = await api.get('/inventory/templates?item_type=domestic');
      setTemplates(templatesResponse.data.items || []);
      setCategories(templatesResponse.data.categories?.domestic || []);
      
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  const addItemFromTemplate = (template) => {
    const existingIndex = inventoryItems.findIndex(
      i => i.item_name === template.name && i.category === template.category
    );
    
    if (existingIndex >= 0) {
      // Increment quantity
      const updated = [...inventoryItems];
      updated[existingIndex].quantity += 1;
      setInventoryItems(updated);
      toast.success(`${template.name} quantity increased`);
    } else {
      // Add new item
      const newItem = {
        id: `temp-${Date.now()}`,
        job_id: jobId,
        category: template.category,
        item_name: template.name,
        quantity: 1,
        is_fragile: template.is_fragile || false,
        needs_packing: false,
        photo_url: null,
        qr_code: null,
        notes: '',
        isNew: true
      };
      setInventoryItems([...inventoryItems, newItem]);
      toast.success(`${template.name} added`);
    }
  };

  const updateItemQuantity = (index, newQuantity) => {
    if (newQuantity < 1) {
      removeItem(index);
      return;
    }
    const updated = [...inventoryItems];
    updated[index].quantity = newQuantity;
    setInventoryItems(updated);
  };

  const removeItem = (index) => {
    const updated = [...inventoryItems];
    updated.splice(index, 1);
    setInventoryItems(updated);
    toast.info('Item removed');
  };

  const handlePhotoCapture = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !currentItem) return;
    
    // Preview
    const reader = new FileReader();
    reader.onloadend = () => setPhotoPreview(reader.result);
    reader.readAsDataURL(file);
    
    // Upload photo
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('item_id', currentItem.id);
      
      // For now, store as base64 (in production, upload to GCS)
      const index = inventoryItems.findIndex(i => i.id === currentItem.id);
      if (index >= 0) {
        const updated = [...inventoryItems];
        updated[index].photo_url = reader.result; // Store base64 temporarily
        setInventoryItems(updated);
        toast.success('Photo added');
      }
    } catch (error) {
      toast.error('Failed to upload photo');
    }
    
    setShowPhotoDialog(false);
    setCurrentItem(null);
    setPhotoPreview(null);
  };

  const handleQRScan = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !currentItem) return;
    
    // In a real implementation, decode the QR code
    // For now, generate a unique code
    try {
      const index = inventoryItems.findIndex(i => i.id === currentItem.id);
      if (index >= 0) {
        const updated = [...inventoryItems];
        updated[index].qr_code = `QR-${Date.now().toString(36).toUpperCase()}`;
        setInventoryItems(updated);
        toast.success('QR code linked');
      }
    } catch (error) {
      toast.error('Failed to scan QR code');
    }
    
    setShowQRDialog(false);
    setCurrentItem(null);
  };

  const togglePackingService = (index) => {
    const updated = [...inventoryItems];
    updated[index].needs_packing = !updated[index].needs_packing;
    setInventoryItems(updated);
    toast.success(updated[index].needs_packing ? 'Packing service requested' : 'Packing service removed');
  };

  const handleAIScan = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setAIScanning(true);
    
    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Image = reader.result.split(',')[1];
        
        try {
          const response = await api.post('/inventory/ai-scan', {
            image: base64Image,
            room_type: 'general'
          });
          
          if (response.data.items && response.data.items.length > 0) {
            const newItems = response.data.items.map((item, idx) => ({
              id: `ai-${Date.now()}-${idx}`,
              job_id: jobId,
              category: item.category || 'Other',
              item_name: item.name,
              quantity: item.quantity || 1,
              is_fragile: item.is_fragile || false,
              needs_packing: false,
              photo_url: null,
              qr_code: null,
              notes: '',
              isNew: true
            }));
            
            setInventoryItems([...inventoryItems, ...newItems]);
            toast.success(`Found ${newItems.length} items!`);
          } else {
            toast.info('No items detected in the image');
          }
        } catch (error) {
          console.error('AI scan error:', error);
          toast.error('AI scan failed. Please add items manually.');
        }
        
        setAIScanning(false);
        setShowAIDialog(false);
      };
      reader.readAsDataURL(file);
      
    } catch (error) {
      toast.error('Failed to process image');
      setAIScanning(false);
    }
  };

  const saveInventory = async () => {
    setSaving(true);
    
    try {
      // Save each new item
      for (const item of inventoryItems) {
        if (item.isNew) {
          await api.post('/inventory', {
            job_id: jobId,
            category: item.category,
            item_name: item.item_name,
            quantity: item.quantity,
            is_fragile: item.is_fragile,
            needs_packing: item.needs_packing,
            photo_url: item.photo_url,
            qr_code: item.qr_code,
            notes: item.notes
          });
        }
      }
      
      toast.success('Inventory saved successfully!');
      navigate('/mobile/dashboard');
      
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save inventory');
    } finally {
      setSaving(false);
    }
  };

  const filteredTemplates = templates.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50 flex items-center justify-center">
        <div className="text-center">
          <Package className="animate-pulse mx-auto text-primary mb-4" size={48} />
          <p className="text-gray-500">Loading inventory...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50" data-testid="customer-inventory-page">
      {/* Header */}
      <header className="bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate('/mobile/dashboard')}>
                <ArrowLeft size={20} />
              </Button>
              <div>
                <h1 className="text-lg font-bold">My Inventory</h1>
                <p className="text-xs text-gray-500">{job?.job_number}</p>
              </div>
            </div>
            <Badge variant="outline">
              {inventoryItems.length} items
            </Badge>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="p-4 pb-32 space-y-4">
        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-3">
          <Button
            variant="outline"
            className="h-20 flex-col"
            onClick={() => setShowAddDialog(true)}
            data-testid="add-from-list-btn"
          >
            <Plus size={24} className="mb-1" />
            <span className="text-sm">Add from List</span>
          </Button>
          <Button
            variant="outline"
            className="h-20 flex-col"
            onClick={() => setShowAIDialog(true)}
            data-testid="ai-scan-btn"
          >
            <Sparkles size={24} className="mb-1" />
            <span className="text-sm">AI Scanner</span>
          </Button>
        </div>

        {/* Category Horizontal Scroll */}
        <Card>
          <CardContent className="py-3">
            <ScrollArea className="w-full whitespace-nowrap">
              <div className="flex gap-2 pb-2" data-testid="category-scroll">
                <Button
                  variant={selectedCategory === 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedCategory('all')}
                  className="shrink-0"
                >
                  All
                </Button>
                {categories.map((cat) => {
                  const IconComponent = CATEGORY_ICONS[cat] || Box;
                  return (
                    <Button
                      key={cat}
                      variant={selectedCategory === cat ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => {
                        setSelectedCategory(cat);
                        setShowAddDialog(true);
                      }}
                      className="shrink-0"
                    >
                      <IconComponent size={14} className="mr-1" />
                      {cat}
                    </Button>
                  );
                })}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Inventory List */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Package size={18} />
              Your Items
            </CardTitle>
            <CardDescription>
              {inventoryItems.reduce((sum, i) => sum + i.quantity, 0)} total items
            </CardDescription>
          </CardHeader>
          <CardContent>
            {inventoryItems.length > 0 ? (
              <div className="space-y-3">
                {inventoryItems.map((item, idx) => (
                  <div 
                    key={item.id} 
                    className={`p-3 border rounded-lg ${
                      item.is_fragile ? 'bg-orange-50 border-orange-200' : ''
                    } ${item.needs_packing ? 'ring-2 ring-blue-300' : ''}`}
                    data-testid={`inventory-item-${idx}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium text-sm">{item.item_name}</p>
                          {item.is_fragile && (
                            <Badge variant="destructive" className="text-xs">
                              <AlertTriangle size={10} className="mr-1" />
                              Fragile
                            </Badge>
                          )}
                          {item.needs_packing && (
                            <Badge variant="secondary" className="text-xs">
                              <Shield size={10} className="mr-1" />
                              Packing
                            </Badge>
                          )}
                          {item.qr_code && (
                            <Badge variant="outline" className="text-xs">
                              <QrCode size={10} className="mr-1" />
                              QR
                            </Badge>
                          )}
                          {item.photo_url && (
                            <Badge variant="outline" className="text-xs">
                              <ImageIcon size={10} className="mr-1" />
                              Photo
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-gray-500">{item.category}</p>
                      </div>
                      
                      {/* Quantity Controls */}
                      <div className="flex items-center gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => updateItemQuantity(idx, item.quantity - 1)}
                        >
                          -
                        </Button>
                        <span className="w-8 text-center font-semibold text-sm">{item.quantity}</span>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => updateItemQuantity(idx, item.quantity + 1)}
                        >
                          +
                        </Button>
                      </div>
                    </div>
                    
                    {/* Item Actions */}
                    <div className="flex gap-2 mt-3 flex-wrap">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setCurrentItem(item);
                          setShowPhotoDialog(true);
                        }}
                        data-testid={`photo-btn-${idx}`}
                      >
                        <Camera size={14} className="mr-1" />
                        Photo
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setCurrentItem(item);
                          setShowQRDialog(true);
                        }}
                        data-testid={`qr-btn-${idx}`}
                      >
                        <QrCode size={14} className="mr-1" />
                        QR Code
                      </Button>
                      {item.is_fragile && (
                        <Button
                          variant={item.needs_packing ? "default" : "outline"}
                          size="sm"
                          onClick={() => togglePackingService(idx)}
                          data-testid={`packing-btn-${idx}`}
                        >
                          <Shield size={14} className="mr-1" />
                          {item.needs_packing ? 'Packing ✓' : 'Request Packing'}
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeItem(idx)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <Package size={48} className="mx-auto mb-4 opacity-50" />
                <p className="font-medium">No items added yet</p>
                <p className="text-sm mt-1">Use the buttons above to add items</p>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Fixed Save Button */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-4 safe-area-bottom">
        <Button 
          className="w-full h-12" 
          onClick={saveInventory}
          disabled={saving || inventoryItems.length === 0}
          data-testid="save-inventory-btn"
        >
          {saving ? 'Saving...' : (
            <>
              <Save size={18} className="mr-2" />
              Save Inventory ({inventoryItems.length} items)
            </>
          )}
        </Button>
      </div>

      {/* Add Item Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-lg max-h-[85vh]">
          <DialogHeader>
            <DialogTitle>Add Items</DialogTitle>
            <DialogDescription>Select items from the list or search</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3">
            <Input
              placeholder="Search items..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="search-items-input"
            />
            
            {/* Category Pills */}
            <ScrollArea className="w-full whitespace-nowrap">
              <div className="flex gap-2 pb-2">
                <Badge
                  variant={selectedCategory === 'all' ? 'default' : 'outline'}
                  className="cursor-pointer shrink-0"
                  onClick={() => setSelectedCategory('all')}
                >
                  All
                </Badge>
                {categories.map((cat) => (
                  <Badge
                    key={cat}
                    variant={selectedCategory === cat ? 'default' : 'outline'}
                    className="cursor-pointer shrink-0"
                    onClick={() => setSelectedCategory(cat)}
                  >
                    {cat}
                  </Badge>
                ))}
              </div>
            </ScrollArea>
            
            <ScrollArea className="h-[350px]">
              <div className="space-y-2 pr-4">
                {filteredTemplates.slice(0, 50).map((item, idx) => (
                  <div
                    key={`${item.name}-${idx}`}
                    className="p-3 border rounded-lg flex justify-between items-center hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => addItemFromTemplate(item)}
                    data-testid={`template-item-${idx}`}
                  >
                    <div className="flex-1">
                      <p className="font-medium text-sm">{item.name}</p>
                      <p className="text-xs text-gray-500">{item.category}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {item.is_fragile && (
                        <Badge variant="destructive" className="text-xs">Fragile</Badge>
                      )}
                      <Plus size={18} className="text-primary" />
                    </div>
                  </div>
                ))}
                {filteredTemplates.length === 0 && (
                  <p className="text-center text-gray-500 py-8">No items found</p>
                )}
              </div>
            </ScrollArea>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Photo Capture Dialog */}
      <Dialog open={showPhotoDialog} onOpenChange={setShowPhotoDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="text-primary" />
              Add Photo
            </DialogTitle>
            <DialogDescription>
              Take a photo of {currentItem?.item_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {photoPreview ? (
              <img src={photoPreview} alt="Preview" className="w-full rounded-lg" />
            ) : (
              <div className="border-2 border-dashed rounded-lg p-8 text-center">
                <Camera size={48} className="mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600">Tap to capture photo</p>
              </div>
            )}
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={handlePhotoCapture}
            />
            
            <Button 
              className="w-full" 
              onClick={() => fileInputRef.current?.click()}
            >
              <Camera size={18} className="mr-2" />
              {photoPreview ? 'Retake Photo' : 'Take Photo'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={showQRDialog} onOpenChange={setShowQRDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="text-primary" />
              Link QR Code
            </DialogTitle>
            <DialogDescription>
              Scan a QR code to link to {currentItem?.item_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="border-2 border-dashed rounded-lg p-8 text-center">
              <QrCode size={48} className="mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600">Scan the QR code from your item label</p>
              <p className="text-xs text-gray-400 mt-2">
                QR codes can be generated by your moving company
              </p>
            </div>
            
            <input
              ref={qrInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={handleQRScan}
            />
            
            <Button 
              className="w-full" 
              onClick={() => qrInputRef.current?.click()}
            >
              <QrCode size={18} className="mr-2" />
              Scan QR Code
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Scanner Dialog */}
      <Dialog open={showAIDialog} onOpenChange={setShowAIDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="text-primary" />
              AI Inventory Scanner
            </DialogTitle>
            <DialogDescription>
              Take a photo of your room and AI will identify items
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {aiScanning ? (
              <div className="text-center py-8">
                <Sparkles size={48} className="mx-auto text-primary animate-pulse mb-4" />
                <p className="font-medium">Analyzing image...</p>
                <p className="text-sm text-gray-500">This may take a few seconds</p>
              </div>
            ) : (
              <>
                <div className="border-2 border-dashed rounded-lg p-8 text-center">
                  <Camera size={48} className="mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600 mb-2">
                    Take a photo of your room
                  </p>
                  <p className="text-xs text-gray-400">
                    Our AI will automatically detect furniture and items
                  </p>
                </div>
                
                {/* Camera input - opens device camera directly */}
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  id="ai-scanner-camera"
                  onChange={handleAIScan}
                />
                
                {/* Gallery input - opens photo gallery */}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  id="ai-scanner-gallery"
                  onChange={handleAIScan}
                />
                
                <Button 
                  className="w-full" 
                  onClick={() => document.getElementById('ai-scanner-camera')?.click()}
                  data-testid="ai-camera-btn"
                >
                  <Camera size={18} className="mr-2" />
                  Open Camera
                </Button>
                
                <Button 
                  variant="outline"
                  className="w-full" 
                  onClick={() => document.getElementById('ai-scanner-gallery')?.click()}
                  data-testid="ai-gallery-btn"
                >
                  <Upload size={18} className="mr-2" />
                  Choose from Gallery
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerInventoryPage;
